import React, {startTransition, useState} from "react";
import {Controller, useForm} from "react-hook-form";

import * as specificationApi from "../../api/equipmentApi/specificationApi";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useRecoilValue} from "recoil";
import {tokenSelector} from "../../Recoil/Atom/authState";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {useGetQuery} from "../../hooks/useQueryHooks";
import {typeValue} from "../../models/clientModels/data/selectList";
import {ResponseList} from "../../models/clientModels/interfaces/Response";
import {
  EQSpecification,
  EquipmentListParams,
  initialEquipmentListParams,
} from "../../models/serverModels/equipment/EQSpecification";
import {FilterGroup, SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1500px",
}));

const SelectSpecification = () => {
  const navigate = useNavigate();

  const tokenState = useRecoilValue(tokenSelector);

  const [page, setPage] = useState<number>(1);

  const [filter, setFilter] = useState<EquipmentListParams>({
    ...initialEquipmentListParams,
    token: tokenState,
    type: 1000,
  });

  const {data, isLoading, isFetching} = useGetQuery<ResponseList<EQSpecification>>({
    key: ["EquipmentList", filter],
    api: () => specificationApi.getList(filter),
  });

  const {control, reset, setValue} = useForm<EquipmentListParams>({
    defaultValues: filter,
  });

  const selectChangeHandler = async (event: SelectChangeEvent<any>) => {
    startTransition(() => {
      setValue("type", event.target.value);
      setFilter({...filter, page: 1, type: event.target.value});
    });
  };

  const specificationFilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const resetHandler = () => {
    setFilter({...initialEquipmentListParams, type: 1000});

    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle="설비선택" />
      <SubMenuGroup>
        <FilterGroup>
          <Box>
            <Controller
              name="type"
              control={control}
              render={({field}) => (
                <FormControl>
                  <InputLabel id="typeLabel">설비분류</InputLabel>
                  <Select
                    style={{backgroundColor: "#fff"}}
                    {...field}
                    labelId="typeLabel"
                    id="type"
                    label="설비분류"
                    size="small"
                    defaultValue={field.value}
                    onChange={selectChangeHandler}
                  >
                    <MenuItem disabled value={field.value} style={{display: "none"}}>
                      선택하세요
                    </MenuItem>
                    {typeValue.map((item) => (
                      <MenuItem
                        style={{backgroundColor: "#fff"}}
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Box>
            <Controller
              name="identifyNumber"
              control={control}
              render={({field}) => (
                <TextField
                  style={{backgroundColor: "#fff"}}
                  {...field}
                  label="설비번호"
                  size="small"
                  onChange={specificationFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="name"
              control={control}
              render={({field}) => (
                <TextField
                  style={{backgroundColor: "#fff"}}
                  {...field}
                  label="설비명"
                  size="small"
                  onChange={specificationFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="eqLevel"
              control={control}
              render={({field}) => (
                <TextField
                  style={{backgroundColor: "#fff"}}
                  {...field}
                  label="설비등급"
                  size="small"
                  name="eqLevel"
                  onChange={specificationFilteringHandler}
                />
              )}
            />
          </Box>
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            size="small"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </FilterGroup>
      </SubMenuGroup>
      {data?.items.length !== 0 && (
        <Box>
          {/* 설비이력카드목록 테이블 */}
          <StyledTableContainer>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell align="center">설비분류</TableCell>
                  <TableCell align="center">설비번호</TableCell>
                  <TableCell align="center">설비명</TableCell>
                  <TableCell align="center">설비등급</TableCell>
                  <TableCell align="center">설비선택</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {isLoading && isFetching ? (
                  <MWSkeleton row={5} column={13} />
                ) : (
                  data?.items.map((item) => (
                    <TableRow key={item.uid}>
                      <TableCell align="center">
                        {typeValue.map((v) => (v.value === item.type ? v.label : ""))}
                      </TableCell>
                      <TableCell align="center">{item.identifyNumber}</TableCell>
                      <TableCell align="center">{item.name}</TableCell>
                      <TableCell align="center">{item.eqLevel}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          sx={{marginRight: "8px"}}
                          onClick={() => {
                            navigate(`/WorkOrder/Create/${item.uid}`, {
                              state: item.uid,
                            });
                          }}
                        >
                          선택
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* 페이지네이션 */}
          <PaginationContainer>
            <Pagination
              count={data?.maxPage}
              page={page}
              onChange={paginationHandleChange}
              variant="outlined"
              color="primary"
              shape="rounded"
            />
          </PaginationContainer>
        </Box>
      )}
    </ListContainer>
  );
};

export default SelectSpecification;
